import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ShopifyAPI } from "~/utils/api.ts";
import API_URLS from "~/utils/urls.ts";
import {RootState} from "~/store/index.ts";

export interface BillingStatus {
    user: string | null;
    billing_plan_id: string | null;
    billing_price_id: string | null;
    order: number | null;
    remaining_images: number | null;
    remaining_models: number | null;
    billing_valid: boolean;
}

interface BillingState {
    loading: boolean;
    error: any;
    requiresBilling: boolean;
    currentPlan: {
        status: string;
        charge_id: string;
    } | null;
    availablePlans: Array<{
        id: number;
        name: string;
        description: string;
        price: number;
        trial_days: number;
        is_recurring: boolean;
        is_credit_based: boolean;
        credits: number;
    }>;
    billingStatus: BillingStatus;
}

const initialState: BillingState = {
    loading: false,
    error: null,
    requiresBilling: false,
    currentPlan: null,
    availablePlans: [],
    billingStatus: {
        remaining_images: 0,
        remaining_models: 0,
        user: null,
        billing_plan_id: null,
        billing_price_id: null,
        order: 0,
        billing_valid: false,
    },
};

export const checkBillingStatus = createAsyncThunk(
    'billing/checkStatus',
    async (shop: string) => {
        const response = await ShopifyAPI.get(API_URLS.BILLING.STATUS, { params: { shop } });
        return response.data;
    }
);

export const createBilling = createAsyncThunk(
    'billing/create',
    async ({ shop, planId }: { shop: string; planId: number }) => {
        const response = await ShopifyAPI.post(API_URLS.BILLING.CREATE, { shop, plan_id: planId });
        return response.data;
    }
);

export const cancelBilling = createAsyncThunk(
    'billing/cancel',
    async (shop: string) => {
        await ShopifyAPI.post(API_URLS.BILLING.CANCEL, { shop });
    }
);

export const fetchBillingStatus = createAsyncThunk<
    BillingStatus,
    void,
    { rejectValue: string }
>(
    'billing/fetchBillingStatus',
    async (_, { rejectWithValue }) => {
        try {
            const response = await ShopifyAPI.get('/billing/check/');
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to check billing status');
        }
    }
);

const billingSlice = createSlice({
    name: 'billing',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(checkBillingStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(checkBillingStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.requiresBilling = action.payload.requires_billing;
                state.currentPlan = action.payload.current_plan;
                state.availablePlans = action.payload.available_plans;
            })
            .addCase(checkBillingStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createBilling.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createBilling.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(createBilling.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchBillingStatus.fulfilled, (state, action) => {
                state.billingStatus = action.payload;
            })
            .addCase(fetchBillingStatus.rejected, (state, action) => {
                state.error = action.payload || 'Failed to check billing status';
                state.billingStatus = {
                    user: null,
                    billing_plan_id: null,
                    billing_price_id: null,
                    order: 0,
                    remaining_images: 0,
                    remaining_models: 0,
                    billing_valid: false,
                }
            });
    },
});

export const selectBillingStatus = (state: RootState) => state.billing.billingStatus;

export default billingSlice.reducer;